import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import MaskedLines from "../common/MaskedLines";
import ProjectCard from "../projects/ProjectCard";
import SmartImage from "../common/SmartImage";
import projects from "../../data/projects";
import { useHasFinePointer } from "../../hooks";
import "./ProjectIndex.css";

/** The two ways of reading the archive. */
const VIEW_MODES = [
  { id: "index", label: "Indeks" },
  { id: "grid", label: "Mreža" },
];

/** Aspect ratios cycled through in grid view so the grid is not a waffle. */
const GRID_RATIO_CYCLE = ["frame--wide", "frame--tall", "frame--wide", "frame--landscape"];

/**
 * The full archive, readable either as a numbered index or as a grid.
 *
 * The index is the primary view. It is the layout architecture practices
 * default to, for a good reason: a visitor scanning for a building type or a
 * town reads a list far faster than a wall of thumbnails. The photograph is
 * not lost, though — on a device with a real pointer, hovering a row raises a
 * preview that tracks the cursor.
 *
 * Performance note: preview images are mounted lazily, one per project, the
 * first time that row is hovered. Mounting all ten up front would download the
 * whole archive's covers for a view that shows none of them.
 *
 * @returns {JSX.Element}
 */
function ProjectIndex() {
  const [activeViewMode, setActiveViewMode] = useState("index");
  const [hoveredProjectId, setHoveredProjectId] = useState(null);
  /** Ids whose preview image has been mounted; grows, never shrinks. */
  const [primedProjectIds, setPrimedProjectIds] = useState(() => new Set());

  const hasFinePointer = useHasFinePointer();
  const previewElementRef = useRef(null);

  /* ------------------------------------------- cursor tracking for preview */
  useEffect(() => {
    if (!hasFinePointer || activeViewMode !== "index") return undefined;

    let pendingFrameId = null;
    let latestPointerX = 0;
    let latestPointerY = 0;

    const applyPreviewPosition = () => {
      pendingFrameId = null;
      const previewElement = previewElementRef.current;
      if (!previewElement) return;
      // Written straight to style rather than through state: this fires on
      // every pointer move, and a re-render per move would be wasteful.
      previewElement.style.transform =
        `translate3d(${latestPointerX}px, ${latestPointerY}px, 0)` +
        ` translate(-50%, -50%)`;
    };

    const handlePointerMove = (pointerEvent) => {
      latestPointerX = pointerEvent.clientX;
      latestPointerY = pointerEvent.clientY;
      if (pendingFrameId !== null) return;
      pendingFrameId = window.requestAnimationFrame(applyPreviewPosition);
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      if (pendingFrameId !== null) window.cancelAnimationFrame(pendingFrameId);
    };
  }, [hasFinePointer, activeViewMode]);

  const handleRowEnter = useCallback(
    (projectId) => {
      setHoveredProjectId(projectId);
      setPrimedProjectIds((previousIds) => {
        if (previousIds.has(projectId)) return previousIds;
        const nextIds = new Set(previousIds);
        nextIds.add(projectId);
        return nextIds;
      });
    },
    []
  );

  const handleRowLeave = useCallback(() => setHoveredProjectId(null), []);

  return (
    <div className="archive">
      {/* ------------------------------------------------------ page header */}
      <header className="archive__header shell">
        <div className="section-head__eyebrow archive__eyebrow">
          <span className="t-label t-numeral section-head__index">
            {String(projects.length).padStart(2, "0")}
          </span>
          <span className="t-label">Arhiva projekata</span>
        </div>

        <MaskedLines
          as="h1"
          lines={["Projekti"]}
          className="archive__title t-display"
        />

        <div className="archive__controls" data-reveal="rise-sm">
          <div
            className="view-switch"
            role="group"
            aria-label="Način prikaza arhive"
          >
            {VIEW_MODES.map((viewMode) => (
              <button
                key={viewMode.id}
                type="button"
                className="view-switch__option"
                aria-pressed={activeViewMode === viewMode.id}
                onClick={() => setActiveViewMode(viewMode.id)}
              >
                {viewMode.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* -------------------------------------------------------- index view */}
      {activeViewMode === "index" && (
        <section className="index-list" aria-label="Projekti, lista">
          <div className="index-list__legend shell" aria-hidden="true">
            <span className="t-label">Br.</span>
            <span className="t-label">Projekat</span>
            <span className="t-label">Tip</span>
            <span className="t-label">Lokacija</span>
            <span className="t-label index-list__legend-end">Šetnja</span>
          </div>

          <ul role="list" className="index-list__rows">
            {projects.map((project, projectIndex) => (
              <li
                className="index-row"
                key={project.id}
                data-reveal="rise-sm"
                style={{ "--reveal-delay": `${Math.min(projectIndex, 8) * 55}ms` }}
                onMouseEnter={() => handleRowEnter(project.id)}
                onMouseLeave={handleRowLeave}
              >
                <div className="index-row__wipe" aria-hidden="true" />
                <Link
                  className="index-row__link shell"
                  to={`/project/${project.slug}`}
                  onFocus={() => handleRowEnter(project.id)}
                  onBlur={handleRowLeave}
                >
                  <span className="index-row__ordinal t-numeral">
                    {String(projectIndex + 1).padStart(2, "0")}
                  </span>

                  <span className="index-row__name">{project.name}</span>

                  <span className="index-row__typology t-meta">
                    {project.typology}
                  </span>

                  <span className="index-row__location t-meta">
                    {project.location ?? "—"}
                  </span>

                  <span className="index-row__vr t-meta">
                    {project.hasVR ? (
                      <span className="index-row__vr-mark">VR</span>
                    ) : (
                      <span aria-hidden="true">—</span>
                    )}
                  </span>

                  <span className="index-row__arrow" aria-hidden="true">
                    →
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {/* Cursor-following preview. Decorative: the row already names the
              project, so this is hidden from assistive technology. */}
          {hasFinePointer && (
            <div
              className="index-preview u-pointer-only"
              ref={previewElementRef}
              data-active={hoveredProjectId !== null ? "true" : "false"}
              aria-hidden="true"
            >
              {projects
                .filter((project) => primedProjectIds.has(project.id))
                .map((project) => (
                  <SmartImage
                    key={project.id}
                    className="index-preview__image"
                    src={project.cover}
                    alt=""
                    sizes="22rem"
                    data-visible={
                      hoveredProjectId === project.id ? "true" : "false"
                    }
                  />
                ))}
            </div>
          )}
        </section>
      )}

      {/* --------------------------------------------------------- grid view */}
      {activeViewMode === "grid" && (
        <section className="archive__grid shell" aria-label="Projekti, mreža">
          {projects.map((project, projectIndex) => (
            <ProjectCard
              key={project.id}
              project={project}
              ordinal={String(projectIndex + 1).padStart(2, "0")}
              ratioClass={GRID_RATIO_CYCLE[projectIndex % GRID_RATIO_CYCLE.length]}
              sizes="(min-width: 64rem) 33vw, (min-width: 48rem) 50vw, 100vw"
            />
          ))}
        </section>
      )}
    </div>
  );
}

export default ProjectIndex;
